import {
  Boxes,
  ChevronsLeft,
  ChevronsRight,
  Cpu,
  DollarSign,
  FlaskConical,
  Globe,
  Grid3x3,
  HardDrive,
  Layers,
  LayoutGrid,
  LogOut,
  MonitorDot,
  Moon,
  Music,
  Network,
  Play,
  RefreshCw,
  Settings,
  ShieldAlert,
  Sun,
  SunMoon,
  TrendingUp,
} from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { OwnerAvatar } from "@/components/owner-avatar";
import { Separator } from "@/components/ui/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/auth-context";
import { useTimezone } from "@/contexts/timezone-context";
import {
  getAutoRefreshDefault,
  setAutoRefreshPersisted,
  useAutoRefresh,
} from "@/hooks/use-auto-refresh";
import { useTheme } from "@/hooks/use-theme";
import { useWiiShopMusic } from "@/hooks/use-wii-shop-music";
import { cn } from "@/lib/utils";

const AUTO_REFRESH_INTERVAL = 5;

const navItems = [
  { to: "/", label: "All Machines", icon: Globe, end: true },
  { to: "/my-machines", label: "My Machines", icon: MonitorDot },
  { to: "/pools", label: "Pools", icon: Cpu },
  { to: "/spot-statistics", label: "Spot statistics", icon: ShieldAlert },
  { to: "/reserved-capacity", label: "Reserved", icon: HardDrive },
  { to: "/k8s-clusters", label: "K8s Clusters", icon: Network },
  { to: "/production-machines", label: "Production", icon: Boxes },
] as const;

const ciNavItems = [
  { to: "/ci", label: "CI Runs", icon: FlaskConical, end: true },
  { to: "/ci/jobs", label: "Jobs", icon: Layers },
  { to: "/ci/overview", label: "Overview", icon: LayoutGrid },
  { to: "/ci/matrix", label: "Matrix", icon: Grid3x3 },
  { to: "/ci/trends", label: "Trends", icon: TrendingUp },
  { to: "/ci/trigger", label: "Trigger", icon: Play },
] as const;

// CI sub-pages (everything under /ci except the bare runs list). Used so the
// "CI Runs" item only highlights for run pages, not its siblings — adding a new
// CI sub-page just needs a ciNavItems entry, no extra guard here.
const ciSubRoutes = ciNavItems.filter((item) => item.to !== "/ci").map((item) => item.to);

const themeOptions = [
  { value: "light" as const, icon: Sun, label: "Light" },
  { value: "dark" as const, icon: Moon, label: "Dark" },
  { value: "system" as const, icon: SunMoon, label: "System" },
];

function AutoRefreshButton({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) {
  const [countdown, setCountdown] = useState(AUTO_REFRESH_INTERVAL);

  useEffect(() => {
    if (!enabled) {
      setCountdown(AUTO_REFRESH_INTERVAL);
      return;
    }
    setCountdown(AUTO_REFRESH_INTERVAL);
    const interval = setInterval(() => {
      setCountdown((previous) => {
        if (previous <= 1) return AUTO_REFRESH_INTERVAL;
        return previous - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [enabled]);

  return (
    <Tooltip>
      <TooltipTrigger>
        <button
          type="button"
          onClick={onToggle}
          className={cn(
            "flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] transition-colors w-full",
            enabled
              ? "text-primary hover:bg-accent"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
          )}
        >
          <RefreshCw
            className={cn("size-4 shrink-0", enabled && "animate-spin")}
            style={
              enabled
                ? {
                    animationDuration: "1.5s",
                    animationTimingFunction: "linear",
                  }
                : undefined
            }
          />
          <span className="group-data-[collapsible=icon]:hidden">
            {enabled ? `${countdown}s` : "Paused"}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">
        {enabled
          ? `Auto-refresh every ${AUTO_REFRESH_INTERVAL}s (click to pause)`
          : "Click to enable auto-refresh"}
      </TooltipContent>
    </Tooltip>
  );
}

function WiiShopButton({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) {
  return (
    <div className="flex items-center gap-1 px-2 py-1.5 group-data-[collapsible=icon]:justify-center">
      <Tooltip>
        <TooltipTrigger>
          <button
            type="button"
            onClick={onToggle}
            className={cn(
              "flex h-7 items-center gap-1.5 rounded-md px-2 transition-colors text-[12px] font-medium",
              enabled
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
          >
            <Music className={cn("size-3.5 shrink-0", enabled && "animate-pulse")} />
            <span className="group-data-[collapsible=icon]:hidden">
              {enabled ? "Now playing" : "Wii Shop"}
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">
          {enabled ? "Wii Shop Channel — click to stop" : "Play the Wii Shop Channel theme"}
        </TooltipContent>
      </Tooltip>
      <span className="ml-1 flex-1 truncate text-[13px] text-muted-foreground group-data-[collapsible=icon]:hidden">
        Music
      </span>
    </div>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [autoRefresh, setAutoRefresh] = useState(getAutoRefreshDefault);
  const { theme, setTheme } = useTheme();
  const { timezone, setTimezone } = useTimezone();
  const { enabled: wiiShopEnabled, toggle: toggleWiiShop } = useWiiShopMusic(user);
  const localMode = user?.local_mode === true;

  useAutoRefresh(autoRefresh);

  const handleToggleAutoRefresh = () => {
    const next = !autoRefresh;
    setAutoRefresh(next);
    setAutoRefreshPersisted(next);
  };

  return (
    <SidebarProvider>
      <Sidebar variant="inset" collapsible="icon">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" className="hover:bg-transparent active:bg-transparent">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="-16 -3 61 61"
                    className="size-5"
                    fill="currentColor"
                  >
                    <g transform="translate(-14 -1.06)">
                      <path d="M29,1.25a.87.87,0,0,0-1.1,0c-2.89,2.38-4.17,5.18-4.51,10h10C33.11,6.43,31.84,3.71,29,1.25Z" />
                      <path d="M14,51.88l.68-3.31a3.94,3.94,0,0,1,1-2l4.59-5.18a5.93,5.93,0,0,0,1.53-3.65l.42-8a16.11,16.11,0,0,1,5.69-11.47.92.92,0,0,1,1.11,0,16,16,0,0,1,5.69,11.47l.42,8a5.93,5.93,0,0,0,1.53,3.65l4.59,5.18a4.8,4.8,0,0,1,1,2L43,51.88c.18.53-.41,1-.85,1l-7.31.51a1.29,1.29,0,0,0-1.27,1.19h0a.92.92,0,0,1-.85.85h-8.5a.85.85,0,0,1-.85-.85h0a1.3,1.3,0,0,0-1.18-1.28l-7.4-.42C14.37,52.9,14,52.56,14,51.88Z" />
                      <path d="M25.38,27.24a.91.91,0,0,0,1.19-.17,2.28,2.28,0,0,1,1.87-.93,2.25,2.25,0,0,1,1.87.93.76.76,0,0,0,.68.34,1.14,1.14,0,0,0,.51-.17.92.92,0,0,0,.17-1.19,4,4,0,0,0-6.46,0A.92.92,0,0,0,25.38,27.24Z" />
                      <path d="M36.85,37.61A4.57,4.57,0,0,0,38,40.24L39.57,42V19.85a3.55,3.55,0,0,0-.51-1.78L37.7,15.86a.87.87,0,0,0-1.44,0L34.9,18.07a3.13,3.13,0,0,0-.51,1.78v2.47a18.4,18.4,0,0,1,2,7.39Z" />
                      <path d="M20,37.61l.43-8a18.6,18.6,0,0,1,2-7.39V19.77A3.68,3.68,0,0,0,22,18l-1.36-2.21a.88.88,0,0,0-1.45,0l-1.27,2.3a3.13,3.13,0,0,0-.51,1.78V42L19,40.24A4,4,0,0,0,20,37.61Z" />
                      <path d="M26.83,17a2.49,2.49,0,0,1,3.23,0,17.47,17.47,0,0,1,3.48,3.91V13H23.34v7.9A17.28,17.28,0,0,1,26.83,17Z" />
                    </g>
                  </svg>
                </div>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="text-sm font-semibold uppercase tracking-[0.1em] text-foreground">
                    Fleet
                  </span>
                  <span className="text-[11px] text-muted-foreground">GPU Management</span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent>
          {!localMode && (
            <SidebarGroup>
              <SidebarGroupLabel className="text-[10px] uppercase tracking-wider">
                Machines
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {navItems.map(({ to, label, icon: Icon, ...rest }) => {
                    const isActive =
                      "end" in rest ? location.pathname === to : location.pathname.startsWith(to);
                    return (
                      <SidebarMenuItem key={to}>
                        <SidebarMenuButton
                          isActive={isActive}
                          tooltip={label}
                          render={<NavLink to={to} end={"end" in rest} />}
                          className={isActive ? "border-l-2 border-primary rounded-l-none" : ""}
                        >
                          <Icon />
                          <span>{label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          <SidebarGroup>
            <SidebarGroupLabel className="text-[10px] uppercase tracking-wider">
              CI
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {ciNavItems.map(({ to, label, icon: Icon }) => {
                  const isActive =
                    to === "/ci"
                      ? location.pathname === "/ci" ||
                        (location.pathname.startsWith("/ci/") &&
                          !ciSubRoutes.some((route) => location.pathname.startsWith(route)))
                      : location.pathname.startsWith(to);
                  return (
                    <SidebarMenuItem key={to}>
                      <SidebarMenuButton
                        isActive={isActive}
                        tooltip={label}
                        render={<NavLink to={to} end={to === "/ci"} />}
                        className={isActive ? "border-l-2 border-primary rounded-l-none" : ""}
                      >
                        <Icon />
                        <span>{label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {!localMode && (
            <SidebarGroup>
              <SidebarGroupLabel className="text-[10px] uppercase tracking-wider">
                Admin
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {user?.is_admin && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={location.pathname === "/spend"}
                        tooltip="Cloud Spend"
                        render={<NavLink to="/spend" />}
                        className={
                          location.pathname === "/spend"
                            ? "border-l-2 border-primary rounded-l-none"
                            : ""
                        }
                      >
                        <DollarSign />
                        <span>Cloud Spend</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={location.pathname === "/settings"}
                      tooltip="Settings"
                      render={<NavLink to="/settings" />}
                      className={
                        location.pathname === "/settings"
                          ? "border-l-2 border-primary rounded-l-none"
                          : ""
                      }
                    >
                      <Settings />
                      <span>Settings</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </SidebarContent>

        <SidebarFooter>
          {/* Theme toggle */}
          <SidebarMenu>
            <SidebarMenuItem>
              <div className="flex items-center gap-1 px-2 py-1.5 group-data-[collapsible=icon]:justify-center">
                {themeOptions.map(({ value, icon: Icon, label }) => (
                  <Tooltip key={value}>
                    <TooltipTrigger>
                      <button
                        type="button"
                        onClick={() => setTheme(value)}
                        className={cn(
                          "flex h-7 w-7 items-center justify-center rounded-md transition-colors",
                          theme === value
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                          value === "system" && "group-data-[collapsible=icon]:hidden",
                        )}
                      >
                        <Icon className="size-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top">{label}</TooltipContent>
                  </Tooltip>
                ))}
                <span className="ml-1 flex-1 truncate text-[13px] text-muted-foreground group-data-[collapsible=icon]:hidden">
                  Theme
                </span>
              </div>
            </SidebarMenuItem>
          </SidebarMenu>

          {/* Timezone toggle */}
          <SidebarMenu>
            <SidebarMenuItem>
              <div className="flex items-center gap-1 px-2 py-1.5 group-data-[collapsible=icon]:justify-center">
                <Tooltip>
                  <TooltipTrigger>
                    <button
                      type="button"
                      onClick={() => setTimezone(timezone === "utc" ? "local" : "utc")}
                      className={cn(
                        "flex h-7 items-center gap-1.5 rounded-md px-2 transition-colors text-[12px] font-medium",
                        "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                      )}
                    >
                      <Globe className="size-3.5 shrink-0" />
                      <span className="group-data-[collapsible=icon]:hidden">
                        {timezone === "utc" ? "UTC" : "Local"}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {timezone === "utc"
                      ? "Showing UTC — click for local time"
                      : "Showing local time — click for UTC"}
                  </TooltipContent>
                </Tooltip>
                <span className="ml-1 flex-1 truncate text-[13px] text-muted-foreground group-data-[collapsible=icon]:hidden">
                  Timezone
                </span>
              </div>
            </SidebarMenuItem>
          </SidebarMenu>

          {/* Auto-refresh */}
          <SidebarMenu>
            <SidebarMenuItem>
              <AutoRefreshButton enabled={autoRefresh} onToggle={handleToggleAutoRefresh} />
            </SidebarMenuItem>
          </SidebarMenu>

          {/* Wii Shop Channel theme (Trololo for Elad) */}
          <SidebarMenu>
            <SidebarMenuItem>
              <WiiShopButton enabled={wiiShopEnabled} onToggle={toggleWiiShop} />
            </SidebarMenuItem>
          </SidebarMenu>

          <Separator />

          {/* User section */}
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" tooltip={user?.name ?? user?.username}>
                <OwnerAvatar
                  name={user?.name ?? user?.username ?? "?"}
                  avatarUrl={user?.avatar_url ?? null}
                  isService={user?.is_service}
                  size="lg"
                />
                <div className="flex flex-1 flex-col gap-0.5 leading-none">
                  <span className="text-[13px] font-medium text-foreground">
                    {user?.name ?? user?.username}
                  </span>
                  <span className="text-[11px] text-muted-foreground">{user?.email}</span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={logout}
                tooltip="Sign out"
                className="text-muted-foreground hover:text-foreground"
              >
                <LogOut />
                <span>Sign out</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>

          <Separator />

          {/* Collapse toggle */}
          <SidebarMenu>
            <SidebarMenuItem>
              <CollapseButton />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>

        <SidebarRail />
      </Sidebar>

      <SidebarInset>
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-4">
          <SidebarTrigger className="md:hidden text-muted-foreground hover:text-foreground" />
          <PageTitle />
        </header>
        <main className="flex-1">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

function CollapseButton() {
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";

  return (
    <SidebarMenuButton onClick={toggleSidebar} tooltip={isCollapsed ? "Expand" : "Collapse"}>
      {isCollapsed ? <ChevronsRight /> : <ChevronsLeft />}
      <span>{isCollapsed ? "Expand" : "Collapse"}</span>
    </SidebarMenuButton>
  );
}

function PageTitle() {
  const location = useLocation();
  const titles: Record<string, string> = {
    "/": "All Machines",
    "/my-machines": "My Machines",
    "/pools": "Pools",
    "/reserved-capacity": "Reserved Capacity",
    "/k8s-clusters": "K8s Clusters",
    "/production-machines": "Production Machines",
    "/spend": "Cloud Spend",
    "/settings": "Settings",
  };
  let title = titles[location.pathname] ?? "";
  if (!title && location.pathname.startsWith("/machines/")) {
    title = "Machine Detail";
  }
  return <span className="text-sm font-medium text-foreground/70">{title}</span>;
}
